/**
 * Permissions Context — Saudi ERP (15 modules, 6 permission levels)
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { logAuditEvent, AuditAction, AuditLevel } from '../../shared/utils/auditLogger';
import { secureGetItem } from '../../shared/utils/secureStorage';
import { isCloudDemoRuntime } from '../../shared/utils/publicDemoRuntime';
import { API_BASE } from '../../shared/utils/apiConfig';
import i18n from '../../i18n';
import {
  PermissionLevels,
  SystemSections,
  DEFAULT_SAUDI_ROLES,
  SAUDI_MODULE_KEYS,
  getModuleDisplayNames,
  comparePermissionLevel,
} from '../config/saudiPermissionsConfig';

const pm = (key, opts) => i18n.t(`serviceMessages.permissions.${key}`, opts);

export { PermissionLevels, SystemSections };

const PermissionsContext = createContext();

function isLegacySeedUser(record) {
  if (!record || typeof record !== 'object') return false;
  const email = String(record.email || '').trim().toLowerCase();
  const displayName = String(record.name || record.username || '').trim();
  const id = record.id != null ? String(record.id) : '';

  const exactLegacyEmails = new Set([
    'admin@qawaem.com',
    'admin@qawaem.sa',
    'admin@qawaem.local',
    'admin@invoit.com',
    'admin@involt.com',
  ]);
  if (exactLegacyEmails.has(email)) return true;
  if (
    id === 'admin' &&
    (displayName === 'Administrateur' || displayName === 'مدير') &&
    /^admin@[^@\s]+\.(qawaem|invoit|involt)\.(com|sa|local)$/i.test(email)
  ) {
    return true;
  }
  return false;
}

function normalizeSecureSessionUser(sess) {
  if (!sess || typeof sess !== 'object') return null;
  const username = sess.username || sess.name;
  const em = String(sess.email || '').trim();
  if (!username && !em) return null;
  const id = sess.id != null && String(sess.id).length ? String(sess.id) : `session_${username || em || 'user'}`;
  const roleRaw = sess.role || 'admin';
  return {
    id,
    name: username || em.split('@')[0] || '',
    nameAr: sess.nameAr || '',
    nameEn: sess.nameEn || username || '',
    username: sess.username || username || '',
    email: em,
    role: roleRaw === 'admin' || roleRaw === 'super_admin' ? 'admin' : roleRaw || 'employee',
    status: sess.status || 'active',
    customPermissions: null,
  };
}

function userListHasMatch(list, candidate) {
  if (!candidate) return false;
  const ce = candidate.email?.toLowerCase();
  const cn = candidate.name?.toLowerCase();
  return list.some(
    (u) =>
      (ce && String(u.email || '').toLowerCase() === ce) ||
      (cn && String(u.name || '').toLowerCase() === cn) ||
      (candidate.id != null && String(u.id) === String(candidate.id)),
  );
}

function pickNonBlank(...values) {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const s = typeof v === 'string' ? v.trim() : String(v).trim();
    if (s !== '') return s;
  }
  return '';
}

/** Normalize legacy array-based permissions to Saudi level strings. */
function normalizePermissionsMap(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const firstVal = Object.values(raw)[0];
  if (typeof firstVal === 'string') return raw;
  if (!Array.isArray(firstVal) && firstVal == null) return raw;

  const out = {};
  for (const key of SAUDI_MODULE_KEYS) {
    const val = raw[key];
    if (typeof val === 'string') {
      out[key] = val;
      continue;
    }
    if (Array.isArray(val)) {
      if (val.includes('add') || val.includes(PermissionLevels.FULL)) out[key] = PermissionLevels.FULL;
      else if (val.includes('edit')) out[key] = PermissionLevels.EDIT;
      else if (val.includes('read') || val.includes('see')) out[key] = PermissionLevels.VIEW;
      else out[key] = PermissionLevels.NONE;
    } else {
      out[key] = PermissionLevels.NONE;
    }
  }
  return out;
}

function mapApiUser(u) {
  return {
    id: u.id?.toString() || `user_${Date.now()}`,
    name: u.name || u.name_ar || u.username || u.name_en || pm('default_user'),
    nameAr: u.name_ar || u.nameAr || '',
    nameEn: u.name_en || u.nameEn || u.username || '',
    username: u.username || '',
    email: u.email || '',
    phone: u.phone || '',
    role: u.role === 'super_admin' ? 'admin' : u.role || 'employee',
    department: u.department || '',
    position: u.position || '',
    nationalId: u.national_id || u.nationalId || '',
    residencyType: u.residency_type || u.residencyType || '',
    status: u.is_active === 0 || u.status === 'inactive' ? 'inactive' : 'active',
    createdAt: u.created_at || u.createdAt || null,
    lastLogin: u.last_login || u.lastLogin || null,
    lastActivity: u.last_activity || u.lastActivity || null,
    notes: u.notes || '',
    customPermissions: normalizePermissionsMap(u.custom_permissions || u.customPermissions),
  };
}

export const PermissionsProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(DEFAULT_SAUDI_ROLES);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPermissionsData();
  }, []);

  const loadPermissionsData = async () => {
    try {
      setLoading(true);

      let rawApiUsers = [];
      let backendUsers = [];
      let adminHintFromSettings = null;

      try {
        if (!isCloudDemoRuntime()) {
          const response = await fetch(`${API_BASE}/users`);
          if (response.ok) {
            const data = await response.json();
            if (data.users && Array.isArray(data.users)) {
              rawApiUsers = data.users;
            }
          }
        }
      } catch (e) {
        console.warn('Could not fetch users from backend API:', e);
      }

      try {
        const { default: settingsService } = await import('../../core/services/settingsService');
        const blob = await settingsService.get();
        const au = blob?.adminUser;
        if (au && (au.username || au.email)) {
          adminHintFromSettings = au;
        }
      } catch (e) {
        console.warn('Could not load adminUser hint from settings service:', e);
      }

      let emailHintLs = '';
      try {
        const hint = typeof localStorage !== 'undefined' ? localStorage.getItem('qawaem_admin_email_hint') : null;
        if (hint && /\S+@\S+\.\S+/.test(hint.trim())) emailHintLs = hint.trim();
      } catch (_) {
        /* ignore */
      }

      if (rawApiUsers.length > 0) {
        backendUsers = rawApiUsers.map((u) => {
          const hintMatch =
            adminHintFromSettings &&
            adminHintFromSettings.username &&
            u.username &&
            String(adminHintFromSettings.username).trim().toLowerCase() ===
              String(u.username).trim().toLowerCase();

          const singleUserFallback =
            rawApiUsers.length === 1 &&
            adminHintFromSettings?.email &&
            (!adminHintFromSettings.username ||
              String(adminHintFromSettings.username).trim().toLowerCase() ===
                String(rawApiUsers[0].username || '').trim().toLowerCase());

          const loneHint = rawApiUsers.length === 1 ? emailHintLs : '';

          const email = pickNonBlank(
            u.email,
            hintMatch ? adminHintFromSettings.email : '',
            singleUserFallback ? adminHintFromSettings.email : '',
            loneHint,
          );

          return mapApiUser({ ...u, email });
        });
      }

      const savedUsers = localStorage.getItem('qawaem_users_permissions');
      const savedRoles = localStorage.getItem('qawaem_custom_roles');

      let realAdminUser = null;
      try {
        const adminUserRaw = localStorage.getItem('qawaem_admin_user');
        if (adminUserRaw) {
          const adminData = JSON.parse(adminUserRaw);
          const uname =
            typeof adminData.username === 'string' ? adminData.username.trim() : String(adminData.username || '').trim();
          const hintUser = adminHintFromSettings?.username
            ? String(adminHintFromSettings.username).trim().toLowerCase()
            : '';
          const hintEmailOk =
            adminHintFromSettings?.email && hintUser && uname && hintUser === uname.toLowerCase();
          if (uname || adminData.email) {
            const aid = adminData.id != null && String(adminData.id).length ? String(adminData.id) : null;
            realAdminUser = {
              id: aid || `admin_${uname || 'local'}`,
              name: uname || String(adminData.email || '').split('@')[0],
              nameAr: adminData.nameAr || adminData.name_ar || '',
              nameEn: adminData.nameEn || adminData.name_en || uname || '',
              username: uname,
              email: pickNonBlank(
                adminData.email,
                hintEmailOk ? adminHintFromSettings.email : '',
                rawApiUsers.length === 1 &&
                  pickNonBlank(uname).toLowerCase() === String(rawApiUsers[0]?.username || '').trim().toLowerCase()
                  ? adminHintFromSettings?.email
                  : '',
                rawApiUsers.length === 1 ? emailHintLs : '',
              ),
              role: 'admin',
              status: 'active',
              customPermissions: null,
            };
            if (isLegacySeedUser(realAdminUser)) realAdminUser = null;
          }
        }
      } catch (e) {
        console.warn('Could not load admin user from qawaem_admin_user:', e);
      }

      let fromStorageFiltered = [];
      if (savedUsers) {
        try {
          const parsed = JSON.parse(savedUsers);
          if (Array.isArray(parsed)) {
            fromStorageFiltered = parsed
              .filter((u) => !isLegacySeedUser(u))
              .map((u) => ({
                ...u,
                customPermissions: normalizePermissionsMap(u.customPermissions),
              }));
          }
        } catch (e) {
          console.warn('Could not parse qawaem_users_permissions:', e);
        }
      }

      let finalUsers = [];
      if (backendUsers.length > 0) {
        finalUsers = [...backendUsers];
      } else if (fromStorageFiltered.length > 0) {
        finalUsers = fromStorageFiltered;
      } else if (realAdminUser) {
        finalUsers = [realAdminUser];
      }

      const sessionUser = normalizeSecureSessionUser(secureGetItem('qawaem_user'));

      finalUsers = finalUsers.map((u) => {
        const uNameLower = String(u.name || u.username || '').toLowerCase();
        let hintMail = '';
        const hintUn = adminHintFromSettings?.username
          ? String(adminHintFromSettings.username).trim().toLowerCase()
          : '';
        if (adminHintFromSettings?.email && hintUn && uNameLower === hintUn) {
          hintMail = String(adminHintFromSettings.email).trim();
        } else if (
          adminHintFromSettings?.email &&
          rawApiUsers.length === 1 &&
          String(rawApiUsers[0]?.username || '').trim().toLowerCase() === uNameLower
        ) {
          hintMail = String(adminHintFromSettings.email).trim();
        }

        let loneLsHint = '';
        if (rawApiUsers.length === 1 && emailHintLs) {
          loneLsHint = emailHintLs;
        }

        if (realAdminUser) {
          const adminNameLower = String(realAdminUser.name || realAdminUser.username || '').toLowerCase();
          const adminEmailLower = String(realAdminUser.email || '').toLowerCase();
          const uEmailLower = String(u.email || hintMail || loneLsHint || '').toLowerCase();
          const idsMatch = String(u.id) === String(realAdminUser.id);
          const namesMatch = uNameLower && adminNameLower && uNameLower === adminNameLower;
          const emailsMatch = uEmailLower && adminEmailLower && uEmailLower === adminEmailLower;

          if (idsMatch || namesMatch || emailsMatch) {
            const mergedMail = pickNonBlank(u.email, hintMail, loneLsHint, realAdminUser.email);
            return {
              ...realAdminUser,
              ...u,
              email: mergedMail,
              name: pickNonBlank(u.name, realAdminUser.name),
            };
          }
        }

        if (sessionUser) {
          const sesNameLower = String(sessionUser.name || sessionUser.username || '').toLowerCase();
          const sesEmailLower = String(sessionUser.email || '').toLowerCase();
          const uEmailLower = String(u.email || hintMail || loneLsHint || '').toLowerCase();
          const idsMatch = String(u.id) === String(sessionUser.id);
          const namesMatch = uNameLower && sesNameLower && uNameLower === sesNameLower;
          const emailsMatch = uEmailLower && sesEmailLower && uEmailLower === sesEmailLower;

          if (idsMatch || namesMatch || emailsMatch) {
            const mergedMail = pickNonBlank(u.email, hintMail, loneLsHint, sessionUser.email);
            return {
              ...sessionUser,
              ...u,
              email: mergedMail,
              name: pickNonBlank(u.name, sessionUser.name),
            };
          }
        }

        if (hintMail || loneLsHint) {
          return { ...u, email: pickNonBlank(u.email, hintMail, loneLsHint) };
        }

        return u;
      });

      if (!userListHasMatch(finalUsers, sessionUser) && sessionUser) {
        finalUsers = [...finalUsers, sessionUser];
      }

      const emailLooksValid = (e) => !!e && /\S+@\S+\.\S+/.test(String(e).trim());

      if (rawApiUsers.length > 0) {
        for (const finalUser of finalUsers) {
          const raw = rawApiUsers.find(
            (r) =>
              String(r?.id ?? '') === String(finalUser?.id ?? '') ||
              String(r.username || '').trim().toLowerCase() === String(finalUser.name || finalUser.username || '').trim().toLowerCase(),
          );
          const mergedMail = pickNonBlank(finalUser.email || '');
          const rawMail = pickNonBlank(raw?.email || '');
          if (raw && emailLooksValid(mergedMail) && !emailLooksValid(rawMail)) {
            try {
              const fixResponse = await fetch(`${API_BASE}/users/${raw.id}/email`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: mergedMail.trim() }),
              });
              if (fixResponse.ok) {
                raw.email = mergedMail.trim();
              }
            } catch (e) {
              console.warn('Could not persist inferred email to database:', e);
            }
          }
        }
      }

      setUsers(finalUsers);
      if (finalUsers.length > 0) {
        await saveUsers(finalUsers);
      } else {
        localStorage.removeItem('qawaem_users_permissions');
        setUsers([]);
      }

      if (savedRoles) {
        try {
          const parsed = JSON.parse(savedRoles);
          setRoles({ ...DEFAULT_SAUDI_ROLES, ...parsed });
        } catch (_) {
          setRoles(DEFAULT_SAUDI_ROLES);
        }
      }

      const adminUser = secureGetItem('qawaem_user');
      if (adminUser && typeof adminUser === 'object') {
        const un = String(adminUser.username || adminUser.name || '').trim().toLowerCase();
        const fromList = finalUsers.find(
          (fu) =>
            String(fu.id ?? '') === String(adminUser.id ?? '') ||
            (!!un && String(fu.name || fu.username || '').trim().toLowerCase() === un),
        );
        const hintAligned =
          adminHintFromSettings?.email &&
          adminHintFromSettings.username &&
          un === String(adminHintFromSettings.username).trim().toLowerCase();

        setCurrentUser({
          id: adminUser.id != null ? String(adminUser.id) : undefined,
          name: adminUser.username || adminUser.name,
          username: adminUser.username,
          email: pickNonBlank(
            adminUser.email,
            fromList?.email,
            hintAligned ? adminHintFromSettings.email : '',
            rawApiUsers.length === 1 &&
              un === String(rawApiUsers[0]?.username || '').trim().toLowerCase()
              ? adminHintFromSettings?.email
              : '',
            rawApiUsers.length === 1 ? emailHintLs : '',
          ),
          role: adminUser.role === 'super_admin' ? 'admin' : adminUser.role || 'admin',
          employeeId: adminUser.employeeId || adminUser.employee_id || undefined,
        });
      } else {
        const legacyRaw = typeof localStorage !== 'undefined' ? localStorage.getItem('qawaem_admin_user') : null;
        if (legacyRaw) {
          try {
            const u = JSON.parse(legacyRaw);
            const un = String(u.username || u.name || '').trim().toLowerCase();
            const fromList = finalUsers.find(
              (fu) => !!un && String(fu.name || fu.username || '').trim().toLowerCase() === un,
            );
            const hintAligned =
              adminHintFromSettings?.email &&
              adminHintFromSettings.username &&
              un === String(adminHintFromSettings.username).trim().toLowerCase();

            setCurrentUser({
              id: u.id != null ? String(u.id) : undefined,
              name: u.username || u.name,
              username: u.username,
              email: pickNonBlank(
                u.email,
                fromList?.email,
                hintAligned ? adminHintFromSettings.email : '',
                rawApiUsers.length === 1 ? emailHintLs : '',
              ),
              role: 'admin',
            });
          } catch (_) {
            /* ignore */
          }
        }
      }
    } catch (error) {
      console.error('Error loading permissions data:', error);
      logAuditEvent(
        AuditAction.SYSTEM_ERROR,
        pm('audit_permissions_system'),
        pm('load_failed'),
        AuditLevel.ERROR,
        { error: error.message },
      );
    } finally {
      setLoading(false);
    }
  };

  const saveUsers = async (updatedUsers) => {
    try {
      localStorage.setItem('qawaem_users_permissions', JSON.stringify(updatedUsers));
      setUsers(updatedUsers);

      logAuditEvent(
        AuditAction.USER_PERMISSION_CHANGE,
        pm('audit_user_permissions'),
        pm('audit_user_permissions_updated'),
        AuditLevel.SUCCESS,
        { usersCount: updatedUsers.length },
      );

      return true;
    } catch (error) {
      console.error('Error saving users:', error);
      return false;
    }
  };

  const saveRoles = async (updatedRoles) => {
    try {
      const customRoles = Object.keys(updatedRoles)
        .filter((roleId) => !Object.keys(DEFAULT_SAUDI_ROLES).includes(roleId))
        .reduce((acc, roleId) => {
          acc[roleId] = updatedRoles[roleId];
          return acc;
        }, {});

      localStorage.setItem('qawaem_custom_roles', JSON.stringify(customRoles));
      setRoles(updatedRoles);

      logAuditEvent(
        AuditAction.USER_ROLE_CHANGE,
        pm('audit_role_management'),
        pm('audit_role_permissions_updated'),
        AuditLevel.SUCCESS,
        { rolesCount: Object.keys(updatedRoles).length },
      );

      return true;
    } catch (error) {
      console.error('Error saving roles:', error);
      return false;
    }
  };

  const hasPermission = (section, permissionLevel = PermissionLevels.VIEW) => {
    if (!currentUser) return false;

    const user = users.find((u) => u.id === currentUser.id);
    if (!user) return false;

    if (user.role === 'admin' || user.role === 'super_admin') return true;

    const required = permissionLevel === PermissionLevels.SEE ? PermissionLevels.VIEW : permissionLevel;

    let effectiveLevel = PermissionLevels.NONE;

    if (user.customPermissions && user.customPermissions[section]) {
      effectiveLevel = user.customPermissions[section];
    } else {
      const role = roles[user.role] || roles.super_admin;
      effectiveLevel = role?.permissions?.[section] ?? PermissionLevels.NONE;
    }

    return comparePermissionLevel(effectiveLevel, required);
  };

  const updateUserPermissions = async (userId, permissionsMap) => {
    const updatedUsers = users.map((user) =>
      user.id === userId ? { ...user, customPermissions: permissionsMap } : user,
    );
    return saveUsers(updatedUsers);
  };

  const updateRolePermissions = async (roleId, permissionsMap) => {
    const updatedRoles = {
      ...roles,
      [roleId]: {
        ...roles[roleId],
        permissions: permissionsMap,
      },
    };
    return saveRoles(updatedRoles);
  };

  const addUser = async (userData) => {
    const newUser = {
      id: `user_${Date.now()}`,
      ...userData,
      status: userData.status || 'active',
      customPermissions: userData.customPermissions || null,
    };
    const updatedUsers = [...users, newUser];
    return saveUsers(updatedUsers);
  };

  const updateUser = async (userId, userData) => {
    const updatedUsers = users.map((user) => (user.id === userId ? { ...user, ...userData } : user));
    return saveUsers(updatedUsers);
  };

  const deleteUser = async (userId) => {
    const updatedUsers = users.filter((user) => user.id !== userId);
    return saveUsers(updatedUsers);
  };

  const getUserEffectivePermissions = (userId) => {
    const user = users.find((u) => u.id === userId);
    if (!user) return {};

    const role = roles[user.role] || roles.admin;
    let effectivePermissions = { ...(role?.permissions || {}) };

    if (user.customPermissions) {
      effectivePermissions = { ...effectivePermissions, ...user.customPermissions };
    }

    return effectivePermissions;
  };

  const getSectionDisplayNames = () => getModuleDisplayNames();

  const value = {
    users,
    roles,
    currentUser,
    loading,
    hasPermission,
    updateUserPermissions,
    updateRolePermissions,
    addUser,
    updateUser,
    deleteUser,
    getUserEffectivePermissions,
    getSectionDisplayNames,
    SystemSections,
    PermissionLevels,
    loadPermissionsData,
  };

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error(pm('hook_outside_provider'));
  }
  return context;
};

export default PermissionsContext;
